import "./CriticalHighRisk.css";

import {
  useOutsideClickEffect,
  useResponsiveValueState,
} from "@saviynt/common";
import {
  ApplicationIcon,
  Box,
  SidePanel,
  Typography,
} from "@saviynt/design-system";
import React, { useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import {
  type AccessMapSuccessResponse,
  type CustomNodeTypes,
  type Findings,
  type NodeData,
} from "../../../types";
import { IconMap } from "../../IconMap";
import { TextWrapper } from "../../TextWrapper";

export default function CriticalHighRisk({
  data,
  isOpen,
  setIsOpen,
}: {
  data: AccessMapSuccessResponse | undefined;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const intl = useIntl();

  const sidepanelRef = useRef(null);
  const [mediaQueryValue, setMediaQueryValue] = useState(0);

  const SCREEN_WIDTH_QUERIES = [
    { query: "(max-width: 833px)", value: 100, label: "TabletMedium" },
  ];

  useResponsiveValueState(setMediaQueryValue, SCREEN_WIDTH_QUERIES);

  const tabletMediumQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === "TabletMedium",
  );

  const isTabletMediumWidth =
    tabletMediumQuery && tabletMediumQuery.value <= mediaQueryValue;

  useOutsideClickEffect(sidepanelRef, () => setIsOpen(false), true);

  const riskCountData = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (data?.data?.nodes?.length) {
      const filteredData = {} as Record<string, NodeData[]>;

      data.data.nodes.forEach((item) => {
        if (item.policyViolationDetails?.length > 0) {
          const label = `${item.label}s`;

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (!filteredData[label]) {
            filteredData[label] = [];
          }

          filteredData[label].push(item);
        }
      });

      const findings: Findings = [];

      Object.keys(filteredData).forEach((risk) => {
        findings.push({
          category: risk as Exclude<CustomNodeTypes, "Owners">,
          findings: filteredData[risk],
        });
      });

      return findings;
    }

    return [];
  }, [data]);

  const sampleLayout = () => (
    <div className="CriticalRisk-SidePanel-Content">
      <Content data={riskCountData} />
    </div>
  );

  return (
    <Box className="Nhi-Inventory-AccessMap-critical-risk-SidePanel">
      <SidePanel
        ref={sidepanelRef}
        header={{
          headerIcon: <ApplicationIcon logo="CriticalHighRiskHexagon" />,
          title: intl.formatMessage({
            description: "Nhi.AccessMap.TopRisks",
            defaultMessage: "Top Findings",
            id: "371uC1",
          }),
        }}
        appIconSize="small"
        layout={sampleLayout()}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        isSidePanelNavigatingList={false}
        className="NHI-Example-SidePanel"
        classNames={{
          layoutClassName: "NHI-SidePanel-layout",
          headerClassName: "NHI-SidePanel-header",
          footerClassName: "NHI-SidePanel-footer",
          innerContentClassName: "NHI-SidePanel-layout-innerContent",
        }}
        dataTestId="NHI-AccessMap-Inventory-Table-SidePanel"
        isTabletWidth={isTabletMediumWidth}
      />
    </Box>
  );
}

function Content({ data }: { data: Findings }) {
  const intl = useIntl();

  return data.length ? (
    data.map((section) => (
      <div className="section" key={section.category}>
        <div className="section-title">
          <IconMap source={section.category} size="medium" className="icon" />
          <Typography kind="body2" desktop="body2-d" mobile="body2-d">
            {section.category}
          </Typography>
        </div>

        {section.findings.map((risk) => (
          <div className="content" key={risk.label}>
            <Typography
              className="section-risk-riskCount"
              kind="body3"
              desktop="body3-d"
              mobile="body3-d"
            >
              {risk.policyViolationDetails.length}
            </Typography>
            <TextWrapper label={risk.displayName} subLabel={risk.type} />
            <Box className="description">
              <Typography kind="body3" desktop="body3-d" mobile="body3-d">
                {risk.policyViolationDetails.map((d) => (
                  <div key={d.name}>{d.name}</div>
                ))}
              </Typography>
            </Box>
          </div>
        ))}
      </div>
    ))
  ) : (
    <Typography className="noResult" kind="h4" desktop="h4-d" mobile="h4-d">
      {intl.formatMessage({
        description: "Nhi.AccessMap.CriticalRisk.NoTopRiskFound",
        defaultMessage: "No Top Findings Found",
        id: "CXb34/",
      })}
    </Typography>
  );
}
